/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import {
	ChangeDetectorRef,
	Component,
	type ComponentRef,
	TemplateRef,
	ViewContainerRef,
	contentChild,
	effect,
	forwardRef,
	inject,
	input,
	linkedSignal,
	output,
	signal,
	viewChild,
} from "@angular/core";
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { LoggerService } from "@app/@services/shared/logger.service";
import { BehaviorSubject, Subject, takeUntil } from "rxjs";
import {
	FORM_ITEMS_COMPONENTS,
	type FormItemData,
} from "../form-item/form-item.component";

const noop = () => { };

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => FormInputComponent),
	multi: true,
};

@Component({
	selector: "cb-form-input",
	templateUrl: "./form-input.component.html",
	styleUrl: "./form-input.component.scss",
	providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
	standalone: false,
	//changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormInputComponent implements ControlValueAccessor {
	private readonly loggerService: LoggerService = inject(LoggerService);

	readonly placeholder = input<string>();
	readonly label = input<string>();
	readonly hint = input<string>();
	readonly info = input<string>();
	readonly required = input(false);
	readonly disabled = input(false);
	readonly error = input(false);
	public readonly data = input<FormItemData>();

	readonly editorOptions = input<Record<string, any>>();

	readonly templateRef = contentChild("ItemTemplate", { read: TemplateRef });

	readonly selectOptionTemplateRef = contentChild("SelectOptionTemplate", {
		read: TemplateRef,
	});

	readonly selectValueTemplateRef = contentChild("SelectValueTemplate", {
		read: TemplateRef,
	});

	readonly templateHost = viewChild("TemplateHost", { read: ViewContainerRef });

	public change = output<unknown>();
	public blur = output<unknown>();

	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public componentRef = signal<ComponentRef<any> | undefined>(undefined);

	private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

	// Inner value of the input
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	private readonly innerValue$: BehaviorSubject<any> = new BehaviorSubject(
		null,
	);
	private innerValue = signal<unknown>(null);

	private readonly destroy$: Subject<void> = new Subject();

	private readonly isDisabled = linkedSignal(() => this.disabled());

	constructor() {
		effect(() => {
			if (this.componentRef()?.instance) {
				this.componentRef()!.setInput("disabled", this.isDisabled());
			}
		});

		effect(() => {
			const options = this.editorOptions();

			if (!options) return;

			if (this.componentRef()?.instance) {
				for (const [key, value] of Object.entries(options)) {
					this.componentRef()!.setInput(key, value);
				}
			}
		});

		effect(() => {
			const componentRef = this.componentRef();
			if (componentRef) {
				const propertyUpdates = {
					placeholder: this.placeholder(),
					label: this.label(),
					hint: this.hint(),
					required: this.required(),
					error: this.error(),
					info: this.info(),
				};

				for (const [key, value] of Object.entries(propertyUpdates)) {
					if (key in componentRef.instance) {
						componentRef.setInput(key, value);
					}
				}
			}

			if (this.data()?.type === "select") {
				this.initSelectTemplates();
			}
		});
	}

	ngAfterViewInit() {
		const templateHost = this.templateHost();
		if (templateHost) {
			templateHost.clear();

			const data = this.data();
			if (data?.type && !(data.type in FORM_ITEMS_COMPONENTS)) return;

			// Render the corresponding component based on the type
			const component = FORM_ITEMS_COMPONENTS[data?.type || "text"];

			if (!component) return;

			this.componentRef.set(templateHost.createComponent(component));

			// Initialize the custom templates like the option/value template of a select
			switch (data?.type) {
				case "select":
					this.initSelectTemplates();
					break;
				// Add here custom template initializers
			}

			const componentRef = this.componentRef();

			if (!componentRef) return;

			// Set the ngModel value in input and output listening for changes
			componentRef.instance.writeValue(this.innerValue());
			componentRef.instance.registerOnChange((newValue: unknown) => {
				this.innerValue.set(newValue);
				this.onChangeCallback(newValue);
			});
			componentRef.instance.registerOnTouched(() => {
				this.onTouchedCallback();

			});

			if (componentRef.instance.change) {
				componentRef.instance.change.subscribe((value: unknown) => {
					this.loggerService.logInfo(
						`Input change detected. ${this.label()}: ${value}`,
					);
					this.change.emit(value);
				});
			}

			if (componentRef.instance.blur) {
				componentRef.instance.blur.subscribe((value: unknown) => {
					this.loggerService.logInfo(
						`Input blur detected. ${this.label()}: ${value}`,
					);
					this.blur.emit(value);
				});
			}

			// Listen for changes in the value and update the component
			this.innerValue$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
				this.innerValue.set(value);
				componentRef.instance.writeValue(value);
			});

			// Set the required flag
			componentRef.setInput("required", this.required());

			// Set the disabled flag
			componentRef.setInput("disabled", this.disabled());

			// Render the component inside the template
			componentRef.instance.templateRef = templateHost;
		}
	}

	/**
	 * Initializes the select templates if they are present
	 * @returns
	 */
	private initSelectTemplates(): void {
		if (this.data()?.type !== "select") return;
		if (!this.componentRef()?.instance) return;

		this.componentRef()!.instance.setOptionTemplate(
			this.selectOptionTemplateRef(),
		);
		this.componentRef()!.instance.setValueTemplate(
			this.selectValueTemplateRef(),
		);
	}

	public writeValue(obj: unknown): void {
		if (
			!this.componentRef()?.instance?.value ||
			obj !== this.componentRef()!.instance.value
		) {
			this.innerValue$.next(obj);
		}
	}

	private onTouchedCallback: () => void = noop;
	private onChangeCallback: (value: unknown) => void = () => { };

	// From ControlValueAccessor interface
	public registerOnChange(fn: (value: unknown) => void) {
		this.onChangeCallback = fn;
	}

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnTouched(fn: any) {
		this.onTouchedCallback = fn;
	}

	public setDisabledState(isDisabled: boolean): void {
		this.isDisabled.set(isDisabled);
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
